<template>
  <Toast></Toast>
  <ConfirmDialog></ConfirmDialog>
  <KnOverlaySpinnerPanel />
  <div class="layout-wrapper-content" :class="{ 'layout-wrapper-content-embed': documentExecution.embed, isMobileDevice: isMobileDevice }">
    <MainMenu v-if="showMenu" @menuItemSelected="setSelectedMenuItem" :closeMenu="closedMenu" @openMenu="openMenu"></MainMenu>

    <div class="layout-main" @click="closeMenu" @blur="closeMenu">
      <router-view :selectedMenuItem="selectedMenuItem" :menuItemClickedTrigger="menuItemClickedTrigger" @click="closeMenu" />
    </div>
  </div>
  <KnRotate v-show="isMobileDevice"></KnRotate>
</template>

<script>
import ConfirmDialog from "primevue/confirmdialog";
import KnOverlaySpinnerPanel from "@/components/UI/KnOverlaySpinnerPanel.vue";
import KnRotate from "@/components/UI/KnRotate.vue";
import MainMenu from "@/modules/mainMenu/MainMenu";
import Toast from "primevue/toast";
import { defineComponent } from "vue";
import store from "@/App.store";
import { mapState } from "vuex";
import WEB_SOCKET from "@/services/webSocket.js";
import themeHelper from "@/helpers/themeHelper/themeHelper";
import { primeVueDate, getLocale } from "@/helpers/commons/localeHelper";
import { loadLanguageAsync } from "@/App.i18n.js";
import auth from "@/helpers/commons/authHelper";

export default defineComponent({
  components: { ConfirmDialog, KnOverlaySpinnerPanel, KnRotate, MainMenu, Toast },

  data() {
    return {
      themeHelper: new themeHelper(),
      selectedMenuItem: null,
      isMobileDevice: false,
      menuItemClickedTrigger: false,
      showMenu: false,
      closedMenu: false,
      pollingInterval: null,
    };
  },

  async beforeCreate() {
    // eslint-disable-next-line no-debugger
    await this.$http
      .get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + "2.0/currentuser")
      .then(async (response) => {
        let currentUser = response.data;
        if (localStorage.getItem("sessionRole")) {
          currentUser.sessionRole = localStorage.getItem("sessionRole");
        } else if (currentUser.defaultRole) currentUser.sessionRole = currentUser.defaultRole;

        store.dispatch("initializeUser", currentUser);

        let responseLocale = response.data.locale;
        let storedLocale = responseLocale;
        if (localStorage.getItem("locale")) {
          storedLocale = localStorage.getItem("locale");
        }
        localStorage.setItem("locale", storedLocale);
        localStorage.setItem("token", response.data.userUniqueIdentifier);

        store.commit("setLocale", storedLocale);
        this.$i18n.locale = storedLocale;

        await loadLanguageAsync(localStorage.getItem("locale"));
        // @ts-ignore
        if (this.$i18n.messages[this.$i18n.locale.replaceAll("-", "_")]) {
          // @ts-ignore
          this.$primevue.config.locale = { ...this.$primevue.config.locale, ...this.$i18n.messages[this.$i18n.locale.replaceAll("-", "_")].locale };
        }
        this.$primevue.config.locale.dateFormat = primeVueDate(getLocale(true));

        let language = this.$i18n;
        let splittedLanguage = language.locale.split("_");

        if (responseLocale !== storedLocale) {
          let url = "/knowage/servlet/AdapterHTTP?";
          url += "ACTION_NAME=CHANGE_LANGUAGE";
          url += "&LANGUAGE_ID=" + splittedLanguage[0];
          url += "&COUNTRY_ID=" + splittedLanguage[1].toUpperCase();
          url += "&SCRIPT_ID=" + (splittedLanguage.length > 2 ? splittedLanguage[2].replaceAll("#", "") : "");
          url += "&THEME_NAME=sbi_default";

          this.$store.commit("setLoading", true);
          this.$http.get(url).then(
            () => {
              store.commit("setLocale", language.locale);
              localStorage.setItem("locale", language.locale);
              this.$i18n.locale = language.locale;
              this.showMenu = true;
            },
            (error) => console.error(error)
          );
        } else {
          this.showMenu = true;
        }
        this.$store.commit("setLoading", false);
      })
      .catch(function(error) {
        if (error.response) {
          console.log(error.response.data);
          console.log(error.response.status);
          console.log(error.response.headers);
        }
      });
    await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + "1.0/user-configs").then((response) => {
      store.commit("setConfigurations", response.data);
      this.checkOIDCSession(response.data);
    });
    if (this.isEnterprise) {
      if (Object.keys(this.defaultTheme.length === 0)) store.commit("setDefaultTheme", await this.themeHelper.getDefaultKnowageTheme());

      await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + "1.0/license").then((response) => {
        store.commit("setLicenses", response.data);
      });
      if (Object.keys(this.theme).length === 0) {
        this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `thememanagement/current`).then((response) => {
          store.commit("setTheme", response.data.config);
          this.themeHelper.setTheme(response.data.config);
        });
      } else {
        this.themeHelper.setTheme(this.theme);
      }
    }
  },
  mounted() {
    this.onLoad();
    if (/Android|iPhone/i.test(navigator.userAgent)) {
      this.isMobileDevice = true;
    }
  },
  beforeUnmount() {
    clearInterval(this.pollingInterval);
  },

  methods: {
    closeDialog() {
      this.$emit("update:visibility", false);
    },
    openMenu() {
      this.closedMenu = false;
    },
    closeMenu() {
      this.closedMenu = true;
    },
    checkOIDCSession(configs) {
      if (configs["oidc.session.polling.url"]) {
        this.pollingInterval = setInterval(async () => {
          let url = configs["oidc.session.polling.url"];
          const parametersRegex = /\${(nonce|client_id|redirect_uri|session_state)}/gm;
          url = url.replace(parametersRegex, (match, parameter) => encodeURIComponent(window.sessionStorage.getItem(parameter)));
          await this.$http
            .get(url, { withCredentials: true })
            .then((response) => {
              if (response.status === 200) {
                const responseURL = new URL(response.request.responseURL);
                if (responseURL.searchParams.get("error")) auth.logout();
              }
            })
            .catch((error) => {
              if (error.response.request.responseURL) {
                const responseURL = new URL(error.response.request.responseURL);
                if (responseURL.searchParams.get("error")) auth.logout();
              }
            });
        }, configs["oidc.session.polling.interval"] || 15000);
      }
    },
    async onLoad() {
      this.$store.commit("setLoading", true);
      await this.$http
        .get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + "2.0/export/dataset")
        .then((response) => {
          let totalDownloads = response.data.length;
          let alreadyDownloaded = response.data.filter((x) => x.alreadyDownloaded).length;

          let json = { downloads: { count: { total: 0, alreadyDownloaded: 0 } } };
          json.downloads.count.total = totalDownloads;
          json.downloads.count.alreadyDownloaded = alreadyDownloaded;

          this.$store.commit("setDownloads", json.downloads);
        })
        await this.$http
          .get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + "2.0/news")
          .then(async (newsResponse) => {
              await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH +'2.0/newsRead').then((newsReadResponse) => {
                  const json = { news: { count: { total: 0, unread: 0 } } }
                  json.news.count.total = newsResponse.data.length
                  json.news.count.unread = newsResponse.data.length - newsReadResponse.data.length
                  this.$store.commit("setNews", json.news);
              })
          })
          .catch((error) => {
            console.log(error)
          })
          .finally(() => {
              this.loadInternationalization()
              this.newsDownloadHandler();
              this.$store.commit("setLoading", false);
          })
    },
    async loadInternationalization() {
      let currentLocale = localStorage.getItem("locale") ? localStorage.getItem("locale") : store.state.locale;
      let currLanguage = "";
      if (currentLocale && Object.keys(currentLocale).length > 0) currentLocale = currentLocale.replaceAll("_", "-");
      else currentLocale = "en-US";

      let splittedLanguage = currentLocale.split("-");
      currLanguage += splittedLanguage[0] + "-";
      if (splittedLanguage.length > 2) currLanguage += splittedLanguage[2].replaceAll("#", "") + "-";
      currLanguage += splittedLanguage[1].toUpperCase();

      await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + "2.0/i18nMessages/internationalization?currLanguage=" + currLanguage).then((response) => store.commit("setInternationalization", response.data));
    },
    newsDownloadHandler() {
      console.log("Starting connection to WebSocket Server");

      WEB_SOCKET.update = function(event) {
        if (event.data) {
          let json = JSON.parse(event.data);
          if (json.news) {
            store.commit("setNews", json.news);
          }
          if (json.downloads) {
            store.commit("setDownloads", json.downloads);
          }
        }
      };
      WEB_SOCKET.onopen = function(event) {
        if (event.data) {
          let json = JSON.parse(event.data);
          if (json.news) {
            store.commit("setNews", json.news);
          }
          if (json.downloads) {
            store.commit("setDownloads", json.downloads);
          }
        }
      };
      WEB_SOCKET.onmessage = function(event) {
        if (event.data) {
          let json = JSON.parse(event.data);
          if (json.news) {
            store.commit("setNews", json.news);
          }
          if (json.downloads) {
            store.commit("setDownloads", json.downloads);
          }
        }
      };
    },
    setSelectedMenuItem(menuItem) {
      this.selectedMenuItem = menuItem;
      this.menuItemClickedTrigger = !this.menuItemClickedTrigger;
    },
  },
  computed: {
    ...mapState({
      error: "error",
      info: "info",
      user: "user",
      warning: "warning",
      loading: "loading",
      isEnterprise: "isEnterprise",
      documentExecution: "documentExecution",
      theme: "theme",
      defaultTheme: "defaultTheme",
    }),
  },
  watch: {
    error(newError) {
      this.$toast.add({
        severity: "error",
        summary: newError.title ? this.$t(newError.title) : "",
        detail: newError.msg ? this.$t(newError.msg) : "",
        baseZIndex: typeof newError.baseZIndex == "undefined" ? 0 : newError.baseZIndex,
        life: typeof newError.duration == "undefined" ? process.env.VUE_APP_TOAST_DURATION : newError.duration,
      });
    },
    info(newInfo) {
      this.$toast.add({
        severity: "info",
        summary: newInfo.title ? this.$t(newInfo.title) : "",
        detail: newInfo.msg ? this.$t(newInfo.msg) : "",
        baseZIndex: typeof newInfo.baseZIndex == "undefined" ? 0 : newInfo.baseZIndex,
        life: typeof newInfo.duration == "undefined" ? process.env.VUE_APP_TOAST_DURATION : newInfo.duration,
      });
    },
    warning(newWarning) {
      this.$toast.add({
        severity: "warn",
        summary: newWarning.title ? this.$t(newWarning.title) : "",
        detail: newWarning.msg ? this.$t(newWarning.msg) : "",
        baseZIndex: typeof newWarning.baseZIndex == "undefined" ? 0 : newWarning.baseZIndex,
        life: typeof newWarning.duration == "undefined" ? process.env.VUE_APP_TOAST_DURATION : newWarning.duration,
      });
    },
    user() {
      /* if (!oldUser.userId && oldUser != newUser)  */
    },
  },
});
</script>

<style lang="scss">
html {
  font-size: var(--kn-font-size);
}
body {
  padding: 0;
  margin: 0;
  font-family: var(--kn-font-family);
}
.layout-wrapper-content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  min-height: 100%;
}
.layout-wrapper-content::after {
  content: "";
  display: table; /* NOTE: Display "block" does not seem to work with height: 0px. */
  height: 0px;
}
.layout-main {
  margin-left: 58px;
  flex: 1;
}
</style>
